// Phase RV-UI — Stake-decision phase narrative (PRD v2.2 §6.1).
//
// Pure function. Returns a single paragraph explaining the agent's
// recommended stake percentage in plain GH₵ terms.
//
// Phase boundaries (PRD v2.2 §6.1):
//   chainWeek 1–3  → Phase 1 "Build" — full base fraction
//   chainWeek 4    → Phase 2 "Transition" — first damp step
//   chainWeek 5+   → Phase 3 "Engine" — withdraw and protect

function fmtGhs(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `GH₵${Math.round(n).toLocaleString()}`;
}

export interface PhaseExplanationInputs {
  readonly chainWeek: number;
  readonly suggestedStake: number;
  readonly winAmount: number;
  readonly bankrollTotal: number;
}

/**
 * Build the phase-explanation paragraph rendered below the Agent
 * recommendation row on the stake decision screen.
 */
export function getPhaseExplanation(input: PhaseExplanationInputs): string {
  const { chainWeek, suggestedStake, winAmount, bankrollTotal } = input;
  const stake = fmtGhs(suggestedStake);
  const win = fmtGhs(winAmount);
  const bank = fmtGhs(bankrollTotal);

  if (chainWeek <= 3) {
    return (
      `You are in Phase 1 — Build. The chain compounds aggressively here, so the ` +
      `agent recommends staking ${stake} of last week's ${win} win to keep the ` +
      `staking power growing roughly 40% per week. The remainder protects your ` +
      `${bank} bankroll. This is the highest-growth phase of the chain.`
    );
  }

  if (chainWeek === 4) {
    return (
      `You are in Phase 2 — Transition. Growth softens deliberately at Week 4: the ` +
      `agent stakes ${stake} of your ${win} win and routes the rest back to your ` +
      `${bank} bankroll. The chain is still compounding, but Phase 2 prepares it ` +
      `for the engine phase by stepping the stake fraction down ~15%.`
    );
  }

  return (
    `You are in Phase 3 — Engine. The chain has built enough staking power that the ` +
    `agent now withdraws more than it stakes. ${stake} goes onto Week ${chainWeek}'s ` +
    `slip and the balance of your ${win} win stays inside the ${bank} bankroll. ` +
    `This is the protection phase — winnings keep accumulating while exposure shrinks.`
  );
}
